/**
 * V2 — platform identity probe: `GET /api/v1/customer-account/me` with credentials only.
 * Signed-in memory is established only after **200** + `customerId`.
 * Durable guest silence retired — no latch short-circuit / latch-on-outcome.
 *
 * G6 — single outbound coordinator: CSP + RQ share one in-flight `/me`
 * (ceiling + land gate). Abort must NOT unrecord ceiling after start.
 */

import type { CustomerMeDTO } from '../../features/account/accountTypes.js';
import type { RuntimeConfig } from '../../config/runtimeConfig.js';
import { isHttpStormLatched } from '../http/httpStormDetectedLatch.js';
import { stormAwareFetch } from '../http/stormAwareFetch.js';
import {
  recordAuthStormCeilingHit,
  recordAuthStormLatchSkip,
  recordAuthStormMeStart,
} from './authStormCounters.js';
import { isLoginLandInFlight } from './loginLandGate.js';
import {
  isMeAttemptCeilingReached,
  recordMeAttemptStart,
} from './meAttemptCeiling.js';

export const ACCOUNT_ME_STALE_MS = 30_000;

/**
 * Wire timeout for boot / peer `/me` — must stay below
 * {@link CUSTOMER_SESSION_BOOT_HYDRATION_WATCHDOG_MS} (12s) so hung `/me` aborts
 * before the CSP watchdog paints guest, avoiding guest→signed-in flicker.
 */
export const PLATFORM_ME_WIRE_TIMEOUT_MS = 10_000;

/**
 * Protocol sentinel for isMissingCredentialUnauthorized / dying-access detection.
 * Align with BE cookie-only message (NE-07 / sessionCredentialExtract).
 * NEVER show this string in customer UI — map via toCustomerFacingErrorMessage /
 * resolvePublicApiFailureMessage → errors.auth.title (G6).
 */
export const MISSING_CUSTOMER_SESSION_CREDENTIAL_MESSAGE =
  'Missing customer session credential (httpOnly cookie required)';

export function isMissingCredentialUnauthorized(
  code: string | undefined,
  message: string | undefined,
): boolean {
  const normalized = (message ?? '').trim().toLowerCase();
  const expected = MISSING_CUSTOMER_SESSION_CREDENTIAL_MESSAGE.toLowerCase();
  if (normalized === expected) {
    return true;
  }
  return (
    code === 'UNAUTHORIZED' &&
    normalized.includes('missing customer session credential')
  );
}

export type PlatformMeResult =
  | { readonly kind: 'ok'; readonly me: CustomerMeDTO }
  | { readonly kind: 'missing_cred' }
  | { readonly kind: 'unauthorized' }
  | { readonly kind: 'ceiling' }
  | { readonly kind: 'transient' };

function resolvePlatformMeUrl(apiBaseUrl: string): string {
  const base = apiBaseUrl.replace(/\/+$/, '');
  return `${base}/api/v1/customer-account/me`;
}

function abortError(): DOMException {
  return new DOMException('The operation was aborted.', 'AbortError');
}

/** Shared in-flight — CSP boot + RQ `useAccountMe` coalesce to one wire GET. */
let inFlightPlatformMe: Promise<PlatformMeResult> | null = null;

/**
 * Last settled `/me` result after single-flight clears. Abort waiters that miss
 * the live inFlight pointer can still establish when shared already proved `ok`.
 */
let lastCompletedPlatformMe: PlatformMeResult | null = null;

/** Test-only — drop coalesced `/me` promise between Jest cases. */
export function resetPlatformMeInFlightForTests(): void {
  inFlightPlatformMe = null;
  lastCompletedPlatformMe = null;
}

/**
 * Peek shared in-flight `/me` (single-flight). Used by CSP Abort path to join
 * the shared result after this-waiter abandons the wait (B7).
 */
export function getPlatformMeInFlight(): Promise<PlatformMeResult> | null {
  return inFlightPlatformMe;
}

/**
 * Peek last completed `/me` after inFlight was cleared (Abort-after-shared-complete).
 */
export function getLastCompletedPlatformMe(): PlatformMeResult | null {
  return lastCompletedPlatformMe;
}

/** Drop retained completed `/me` (logout wipe / test reset). */
export function clearLastCompletedPlatformMe(): void {
  lastCompletedPlatformMe = null;
}

export type FetchPlatformMeOptions = {
  /**
   * When true, stormAwareFetch skips fair-queue (caller already holds a slot —
   * ApiClient.request → fetchMeViaPlatformCoordinator). CSP/boot leave default.
   */
  readonly skipQueue?: boolean;
  /**
   * G6 — land probe from {@link applyAuthenticatedSession}. Required to run `/me`
   * while {@link isLoginLandInFlight}; other callers refuse during that window.
   */
  readonly forLoginLand?: boolean;
  /**
   * Cross-tab login / visibility catch-up: never join an in-flight `/me` that may
   * have left the wire before cookies were minted (boot raced login → stale 401).
   * Waits for any in-flight to settle, then starts a new credentials GET.
   */
  readonly forceFresh?: boolean;
};

async function executePlatformMeFetch(
  apiBaseUrl: string,
  skipQueue: boolean,
): Promise<PlatformMeResult> {
  const url = resolvePlatformMeUrl(apiBaseUrl);
  const timeoutController = new AbortController();
  const timeoutId = window.setTimeout(() => {
    timeoutController.abort();
  }, PLATFORM_ME_WIRE_TIMEOUT_MS);

  try {
    const response = await stormAwareFetch(
      url,
      {
        method: 'GET',
        credentials: 'include',
        headers: { Accept: 'application/json' },
        signal: timeoutController.signal,
      },
      skipQueue ? { skipQueue: true } : undefined,
    );

    type MeResponseJson = {
      success?: boolean;
      data?: CustomerMeDTO;
      code?: string;
      error?: string;
      message?: string;
    };

    let json: MeResponseJson | null = null;
    let code: string | undefined;
    let message: string | undefined;
    try {
      json = (await response.json()) as MeResponseJson;
      code = json.code;
      message =
        (typeof json.error === 'string' ? json.error : undefined) ??
        (typeof json.message === 'string' ? json.message : undefined);
    } catch {
      // non-JSON body
    }

    if (response.status === 401 || response.status === 403) {
      if (isMissingCredentialUnauthorized(code, message)) {
        return { kind: 'missing_cred' };
      }
      return { kind: 'unauthorized' };
    }

    if (!response.ok) {
      return { kind: 'transient' };
    }

    if (json?.success === true && json.data !== undefined) {
      return { kind: 'ok', me: json.data };
    }
    return { kind: 'transient' };
  } catch {
    // G3/G6: Abort must NOT unrecord ceiling — remount must not free budget.
    // Classification stays transient; durable guest latch retired.
    return { kind: 'transient' };
  } finally {
    window.clearTimeout(timeoutId);
  }
}

/**
 * Credentials-only platform `/me`. Concurrent callers share one in-flight
 * promise (single-flight). Caller `signal` only abandons the wait — it does
 * not cancel the shared wire GET or free the ceiling watermark.
 *
 * G6 — while {@link isLoginLandInFlight}, non-land callers refuse wire so they
 * cannot race the land unlock window; land probe passes `forLoginLand: true`.
 */
export async function fetchPlatformMe(
  config: Pick<RuntimeConfig, 'apiBaseUrl'>,
  signal?: AbortSignal,
  options?: FetchPlatformMeOptions,
): Promise<PlatformMeResult> {
  const skipQueue = options?.skipQueue === true;
  const forceFresh = options?.forceFresh === true;
  // G6 — refuse concurrent non-land /me during login land unlock window.
  if (isLoginLandInFlight() && options?.forLoginLand !== true) {
    recordAuthStormLatchSkip();
    return { kind: 'transient' };
  }
  if (signal?.aborted) {
    throw abortError();
  }
  if (isHttpStormLatched()) {
    return { kind: 'transient' };
  }

  // Cross-tab login: drain any boot/other in-flight without adopting its result
  // (request may have been sent before Set-Cookie). Then start a new wire GET.
  if (forceFresh) {
    while (inFlightPlatformMe !== null) {
      const draining = inFlightPlatformMe;
      try {
        await draining;
      } catch {
        // Drain only — forceFresh must not surface Abort from the prior flight.
      }
      if (signal?.aborted) {
        throw abortError();
      }
    }
  }

  if (inFlightPlatformMe !== null) {
    const shared = inFlightPlatformMe;
    if (signal === undefined) {
      return shared;
    }
    return new Promise<PlatformMeResult>((resolve, reject) => {
      const onAbort = (): void => {
        reject(abortError());
      };
      signal.addEventListener('abort', onAbort, { once: true });
      void shared.then(
        (result) => {
          signal.removeEventListener('abort', onAbort);
          if (signal.aborted) {
            reject(abortError());
            return;
          }
          resolve(result);
        },
        (err: unknown) => {
          signal.removeEventListener('abort', onAbort);
          reject(err);
        },
      );
    });
  }

  const meStartAt = Date.now();
  if (isMeAttemptCeilingReached(meStartAt)) {
    recordAuthStormCeilingHit();
    return { kind: 'ceiling' };
  }
  recordMeAttemptStart(meStartAt);
  recordAuthStormMeStart();

  // Drop stale completed result before this flight so Abort cannot revive a prior ok
  // after logout / guest remount (Abort-after-shared-complete still sets last in .then).
  lastCompletedPlatformMe = null;

  const promise = executePlatformMeFetch(config.apiBaseUrl, skipQueue)
    .then((result) => {
      lastCompletedPlatformMe = result;
      return result;
    })
    .finally(() => {
      if (inFlightPlatformMe === promise) {
        inFlightPlatformMe = null;
      }
    });
  inFlightPlatformMe = promise;

  if (signal === undefined) {
    return promise;
  }

  return new Promise<PlatformMeResult>((resolve, reject) => {
    const onAbort = (): void => {
      reject(abortError());
    };
    signal.addEventListener('abort', onAbort, { once: true });
    void promise.then(
      (result) => {
        signal.removeEventListener('abort', onAbort);
        if (signal.aborted) {
          reject(abortError());
          return;
        }
        resolve(result);
      },
      (err: unknown) => {
        signal.removeEventListener('abort', onAbort);
        reject(err);
      },
    );
  });
}
